import { assertIsNotUndefined } from '@gitbook/util-ts';

import { type Collection, isCollection } from '../collection';
import { debugPerformance, perfNow } from '../utils';
import { hashJSValue } from './hashJSValue';
import { hashMerge, murmurHashOfSize } from './math';

/**
 * Mapping of objects to their hash codes.
 */
const HASH_CACHE = new WeakMap<Collection, number>();

/**
 * Hash an immutable collection.
 */
export function hashCollection(collection: Collection): number {
    debugPerformance.hashCollectionsCount++;

    if (HASH_CACHE.has(collection)) {
        debugPerformance.hashCollectionsCachedCount++;
        const hash = HASH_CACHE.get(collection);
        assertIsNotUndefined(hash);
        return hash;
    }

    // @ts-ignore - util-immutable can run in a TS codebase without lib: dom
    const start = perfNow();
    const hash = hashCollectionEntries(collection);
    HASH_CACHE.set(collection, hash);

    // @ts-ignore - util-immutable can run in a TS codebase without lib: dom
    const end = perfNow();
    debugPerformance.hashTime += end - start;

    return hash;
}

/**
 * Compute the hash of all entries in a collection.
 * This function can generate the same hash for 2 different collections.
 */
export function hashCollectionEntries(collection: Collection): number {
    if (collection.size === Number.POSITIVE_INFINITY) {
        return 0;
    }
    const collectionConstructor = collection.constructor;
    const isOrdered =
        'isOrdered' in collectionConstructor &&
        typeof collectionConstructor.isOrdered === 'boolean' &&
        collectionConstructor.isOrdered;
    const isKeyed =
        'isKeyed' in collectionConstructor &&
        typeof collectionConstructor.isKeyed === 'boolean' &&
        collectionConstructor.isKeyed;
    const size = collection.size;

    let h = isOrdered ? 1 : 0;

    collection.iterate((v, k) => {
        if (isKeyed) {
            if (isOrdered) {
                return (h =
                    (31 * h +
                        hashMerge(
                            hashCollectionValue(collection, v),
                            hashCollectionValue(collection, k)
                        )) |
                    0);
            }
            return (h =
                (h +
                    hashMerge(
                        hashCollectionValue(collection, v),
                        hashCollectionValue(collection, k)
                    )) |
                0);
        }
        if (isOrdered) {
            return (h = (31 * h + hashCollectionValue(collection, v)) | 0);
        }
        return (h = (h + hashCollectionValue(collection, v)) | 0);
    });

    return murmurHashOfSize(size, h);
}

/**
 * Compute the hash of a value stored in a collection.
 */
function hashCollectionValue(parentCollection: Collection, value: any): number {
    if (isCollection(value)) {
        return hashCollection(value);
    }
    return hashJSValue(parentCollection, value);
}
